import type { RtcStreamMeasurement } from './rtcStats.ts';
import type { PublishSettingsResult, ReportedNumber } from './publishSettings.ts';

export interface RtcStatsHudSetter {
  (measurement: RtcStreamMeasurement | undefined): void;
  setPublishSettings(settings: PublishSettingsResult | undefined): void;
}

function report(value: ReportedNumber, unit: 'bitrate' | 'fps'): string {
  if (value.status === 'not-reported') return `${unit} not reported`;
  return unit === 'bitrate'
    ? `${(value.value / 1_000_000).toFixed(3)} Mbit/s`
    : `${value.value.toFixed(1)} fps`;
}

export function mountRtcStatsHud(host: HTMLElement, label: string): RtcStatsHudSetter {
  host.classList.add('rtc-stats-hud');
  host.innerHTML = `<span>${label}</span><b data-rtc-summary>Waiting for RTP counters…</b><b data-publish-summary hidden></b>`;
  let latestMeasurement: RtcStreamMeasurement | undefined;
  let latestPublishSettings: PublishSettingsResult | undefined;
  let publishWasSet = false;

  const renderPublishSettings = (): void => {
    const summary = host.querySelector<HTMLElement>('[data-publish-summary]');
    if (!summary || !publishWasSet) return;
    summary.hidden = false;
    if (!latestPublishSettings) {
      summary.textContent = 'Sender limits stopped · readback not reported.';
      delete host.dataset.publishOutcome;
      delete host.dataset.publishRequestedBps;
      delete host.dataset.publishRequestedFps;
      host.dataset.publishEffectiveBps = 'not-reported';
      host.dataset.publishEffectiveFps = 'not-reported';
      return;
    }
    const { requested, outcome, encodings } = latestPublishSettings;
    host.dataset.publishOutcome = outcome;
    host.dataset.publishRequestedBps = String(requested.maxBitrate);
    host.dataset.publishRequestedFps = String(requested.maxFramerate);
    const first = encodings[0];
    host.dataset.publishEffectiveBps = first?.maxBitrate.status === 'reported'
      ? String(first.maxBitrate.value)
      : 'not-reported';
    host.dataset.publishEffectiveFps = first?.maxFramerate.status === 'reported'
      ? String(first.maxFramerate.value)
      : 'not-reported';
    const actual = encodings.length === 0
      ? 'bitrate not reported · fps not reported'
      : encodings.map((encoding) => `e${encoding.index} ${report(encoding.maxBitrate, 'bitrate')} · ${report(encoding.maxFramerate, 'fps')}`).join(' · ');
    const measured = latestMeasurement
      ? `${latestMeasurement.framesPerSecond.toFixed(1)} fps measured by getStats`
      : 'getStats fps pending';
    summary.textContent = `Sender readback ${actual} · requested ${requested.maxFramerate} fps / ${(requested.maxBitrate / 1_000_000).toFixed(3)} Mbit/s · ${measured}`;
  };

  const setMeasurement = ((measurement: RtcStreamMeasurement | undefined) => {
    latestMeasurement = measurement;
    const summary = host.querySelector<HTMLElement>('[data-rtc-summary]');
    if (!measurement) {
      delete host.dataset.rtcDirection;
      delete host.dataset.rtcKbps;
      delete host.dataset.rtcFps;
      delete host.dataset.rtcWidth;
      delete host.dataset.rtcHeight;
      delete host.dataset.rtcSampledAtMs;
      if (summary) summary.textContent = 'Stopped · waiting for RTP counters…';
      renderPublishSettings();
      return;
    }
    host.dataset.rtcDirection = measurement.direction;
    host.dataset.rtcKbps = measurement.kilobitsPerSecond.toFixed(3);
    host.dataset.rtcFps = measurement.framesPerSecond.toFixed(3);
    host.dataset.rtcWidth = String(measurement.width);
    host.dataset.rtcHeight = String(measurement.height);
    host.dataset.rtcSampledAtMs = String(measurement.sampledAtMs);
    if (summary) {
      summary.textContent = `${measurement.kilobitsPerSecond.toFixed(1)} kbit/s · ${measurement.width}×${measurement.height} · ${measurement.framesPerSecond.toFixed(1)} fps`;
    }
    renderPublishSettings();
  }) as RtcStatsHudSetter;

  setMeasurement.setPublishSettings = (settings): void => {
    publishWasSet = true;
    latestPublishSettings = settings;
    renderPublishSettings();
  };
  return setMeasurement;
}
